import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Mailbox, RefreshCcw, PlayCircle, CheckCircle2, XCircle, Loader2,
  Eye, AlertTriangle, FileText, ListChecks, Clock, ChevronRight,
  Settings2, Save, Inbox as InboxIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function relativeTime(iso) {
  if (!iso) return '—';
  try {
    const dt = new Date(iso);
    const diff = Math.floor((Date.now() - dt.getTime()) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return dt.toLocaleDateString();
  } catch { return '—'; }
}

function AdminCommissionFeed() {
  const [tab, setTab] = useState('queue'); // queue | log
  const [status, setStatus] = useState(null);
  const [queue, setQueue] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [filter, setFilter] = useState('pending_review');

  const auth = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  const loadStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/commission-ingest/status`, auth());
      setStatus(r.data);
    } catch { /* non-fatal */ }
  }, []);

  const loadQueue = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API_URL}/api/admin/commission-ingest/queue`,
        { ...auth(), params: { status: filter, limit: 200 } },
      );
      setQueue(r.data?.items || []);
    } catch {
      toast.error('Failed to load queue');
    }
  }, [filter]);

  const loadLog = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API_URL}/api/admin/commission-ingest/log`,
        { ...auth(), params: { limit: 200 } },
      );
      setLog(r.data?.items || []);
    } catch {
      toast.error('Failed to load audit log');
    }
  }, []);

  const reloadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadStatus(), loadQueue(), loadLog()]);
    setLoading(false);
  }, [loadStatus, loadQueue, loadLog]);

  useEffect(() => { reloadAll(); }, [reloadAll]);

  const pollNow = async () => {
    try {
      setPolling(true);
      const r = await axios.post(
        `${API_URL}/api/admin/commission-ingest/poll-now`, {}, auth(),
      );
      const { applied = 0, queued = 0 } = r.data || {};
      if (applied || queued) {
        toast.success(`Poll done · ${applied} auto-applied · ${queued} queued for review`);
      } else {
        toast.success('Poll done · no new commission emails found');
      }
      await reloadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Poll failed');
    } finally {
      setPolling(false);
    }
  };

  const visibleQueue = useMemo(() => queue, [queue]);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-commission-feed-page">
        <Header
          status={status}
          polling={polling}
          onPollNow={pollNow}
          onRefresh={reloadAll}
          refreshing={loading}
        />

        <StatusStrip status={status} pendingCount={queue.filter((q) => q.status === 'pending_review').length} logCount={log.length} />

        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-white/10">
          <TabButton active={tab === 'queue'} onClick={() => setTab('queue')} icon={ListChecks} label="Review queue" count={queue.filter((q) => q.status === 'pending_review').length} testid="tab-queue" />
          <TabButton active={tab === 'log'} onClick={() => setTab('log')} icon={FileText} label="Audit log" count={log.length} testid="tab-log" />
        </div>

        {tab === 'queue' && (
          <>
            <FilterRow value={filter} onChange={setFilter} />
            {loading ? (
              <LoadingState />
            ) : visibleQueue.length === 0 ? (
              <EmptyQueue filter={filter} />
            ) : (
              <div className="space-y-3" data-testid="queue-list">
                {visibleQueue.map((row) => (
                  <QueueCard
                    key={row.id}
                    row={row}
                    onChanged={reloadAll}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {tab === 'log' && (
          loading ? <LoadingState /> :
          log.length === 0 ? <EmptyLog /> :
          <LogTable rows={log} />
        )}
      </div>
    </AdminLayout>
  );
}

function Header({ status, polling, onPollNow, onRefresh, refreshing }) {
  const healthy = status?.enabled && status?.has_credentials;
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Mailbox className="h-6 w-6 text-[#00F0FF]" />
          <h1 className="text-2xl font-bold text-white">Commission Feed</h1>
          {healthy ? (
            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#00FF94]/15 text-[#00FF94] border border-[#00FF94]/30">
              Live
            </span>
          ) : status ? (
            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
              {!status.has_credentials ? 'Missing credentials' : 'Disabled'}
            </span>
          ) : null}
        </div>
        <p className="text-sm text-white/60 max-w-3xl">
          Auto-ingested commission emails from <code className="text-[#00F0FF] text-xs">{status?.from_address || 'Zoho mailbox'}</code>.
          High-confidence parses are auto-applied to Partner Revenue. Medium/low go here for one-click review.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline" size="sm" onClick={onRefresh} disabled={refreshing}
          data-testid="refresh-btn"
          className="border-white/20 text-white hover:bg-white/10"
        >
          <RefreshCcw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
        <Button
          size="sm" onClick={onPollNow} disabled={polling || !status?.enabled}
          data-testid="poll-now-btn"
          className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold"
        >
          {polling
            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Polling…</>
            : <><PlayCircle className="h-4 w-4 mr-2" />Poll now</>}
        </Button>
      </div>
    </div>
  );
}

function StatusStrip({ status, pendingCount, logCount }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="status-strip">
      <Stat
        label="Last processed"
        value={relativeTime(status?.last_processed_at)}
        sub={status?.last_processed_subject ? status.last_processed_subject.slice(0, 38) + '…' : '—'}
        testid="stat-last"
      />
      <Stat
        label="Pending review"
        value={pendingCount}
        sub={pendingCount > 0 ? 'awaiting your confirmation' : 'all clear'}
        accent={pendingCount > 0 ? 'amber' : 'cyan'}
        testid="stat-pending"
      />
      <Stat
        label="Auto-applied (lifetime)"
        value={logCount}
        sub="high-confidence parses"
        accent="green"
        testid="stat-applied"
      />
      <Stat
        label="Allowed senders"
        value={status?.allowed_senders?.length ?? 0}
        sub={(status?.allowed_senders || []).slice(0, 2).join(', ') + (status?.allowed_senders?.length > 2 ? `, +${status.allowed_senders.length - 2}` : '')}
        testid="stat-senders"
      />
    </div>
  );
}

function Stat({ label, value, sub, accent = 'white', testid }) {
  const color = accent === 'green' ? 'text-[#00FF94]'
    : accent === 'amber' ? 'text-amber-300'
    : accent === 'cyan' ? 'text-[#00F0FF]'
    : 'text-white';
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider font-bold text-white/60">{label}</div>
      <div className={`text-2xl font-bold mt-1 truncate ${color}`}>{value}</div>
      <div className="text-xs text-white/50 mt-0.5 truncate" title={sub}>{sub}</div>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, count, testid }) {
  return (
    <button
      type="button" onClick={onClick} data-testid={testid}
      className={`relative flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition ${
        active ? 'text-white' : 'text-white/50 hover:text-white/80'
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
      {count > 0 && (
        <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
          active ? 'bg-[#00F0FF] text-black' : 'bg-white/10 text-white/70'
        }`}>
          {count}
        </span>
      )}
      {active && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00F0FF]" />}
    </button>
  );
}

function FilterRow({ value, onChange }) {
  const opts = [
    { v: 'pending_review', label: 'Pending review' },
    { v: 'applied', label: 'Applied' },
    { v: 'dismissed', label: 'Dismissed' },
    { v: 'all', label: 'All' },
  ];
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {opts.map((o) => (
        <button
          key={o.v} type="button"
          data-testid={`queue-filter-${o.v}`}
          onClick={() => onChange(o.v)}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
            value === o.v
              ? 'bg-[#00F0FF] text-black'
              : 'bg-white/5 text-white/70 hover:bg-white/10'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="rounded-xl border border-white/10 p-10 text-center text-white/50">
      <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Loading…
    </div>
  );
}

function EmptyQueue({ filter }) {
  return (
    <div className="rounded-xl border border-dashed border-white/15 p-10 text-center" data-testid="empty-queue">
      <CheckCircle2 className="h-10 w-10 text-[#00FF94] mx-auto mb-3" />
      <p className="text-base text-white/80 mb-1">
        {filter === 'pending_review'
          ? 'All clear — nothing to review right now.'
          : `No rows with status "${filter}".`}
      </p>
      <p className="text-xs text-white/50">
        High-confidence commission emails go straight to Partner Revenue. Anything ambiguous lands here.
      </p>
    </div>
  );
}

function EmptyLog() {
  return (
    <div className="rounded-xl border border-dashed border-white/15 p-10 text-center" data-testid="empty-log">
      <InboxIcon className="h-10 w-10 text-white/40 mx-auto mb-3" />
      <p className="text-base text-white/80 mb-1">No auto-applied commissions yet.</p>
      <p className="text-xs text-white/50">
        Once Gmail forwards a Rewardful/PartnerStack/etc. commission email, the auto-applied entries appear here.
      </p>
    </div>
  );
}

function QueueCard({ row, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [edit, setEdit] = useState({
    partner_slug: row.parsed?.partner_slug || row.parsed?.partner_slug_unmatched || '',
    revenue_usd: row.parsed?.revenue_usd ?? '',
    conversions: row.parsed?.conversions ?? 1,
    period_year: row.parsed?.period_year ?? new Date().getFullYear(),
    period_month: row.parsed?.period_month ?? (new Date().getMonth() + 1),
  });
  const [busy, setBusy] = useState(false);
  const status = row.status;
  const parsed = row.parsed || {};
  const isReview = status === 'pending_review';

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const apply = async () => {
    if (!edit.partner_slug?.trim()) {
      toast.error('Partner slug is required');
      return;
    }
    try {
      setBusy(true);
      const r = await axios.post(
        `${API_URL}/api/admin/commission-ingest/queue/${row.id}/apply`,
        {
          partner_slug: edit.partner_slug.trim().toLowerCase(),
          revenue_usd: Number(edit.revenue_usd),
          conversions: Number(edit.conversions),
          period_year: Number(edit.period_year),
          period_month: Number(edit.period_month),
        },
        auth(),
      );
      toast.success(`Applied · revenue row ${r.data?.revenue_row_id}`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    try {
      setBusy(true);
      await axios.post(`${API_URL}/api/admin/commission-ingest/queue/${row.id}/dismiss`, {}, auth());
      toast.success('Dismissed');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dismiss failed');
    } finally {
      setBusy(false);
    }
  };

  const confidence = (parsed.confidence || 'low').toLowerCase();
  const conf = confidence === 'high'
    ? 'bg-[#00FF94]/15 text-[#00FF94] border-[#00FF94]/30'
    : confidence === 'medium'
    ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
    : 'bg-red-500/15 text-red-300 border-red-500/30';

  return (
    <article
      className={`rounded-xl border bg-white/[0.03] overflow-hidden ${
        status === 'pending_review' ? 'border-white/10' : 'border-white/5 opacity-70'
      }`}
      data-testid={`queue-card-${row.id}`}
    >
      <div className="p-4 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-bold text-white text-sm truncate">{row.subject || '(no subject)'}</h3>
            <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${conf}`}>
              {confidence} confidence
            </span>
            <StatusPill status={status} />
          </div>
          <p className="text-xs text-white/50 truncate">
            From <code className="text-white/70">{row.sender}</code> · {relativeTime(row.created_at)}
          </p>
          {parsed.reasoning && (
            <p className="text-xs text-white/60 mt-1 italic">{parsed.reasoning}</p>
          )}
          {parsed.partner_slug_unmatched && (
            <p className="text-xs text-amber-300 mt-1">
              ⚠ Suggested slug <code>{parsed.partner_slug_unmatched}</code> not in directory — pick a real one below.
            </p>
          )}
          {parsed.error && (
            <p className="text-xs text-red-300 mt-1">Parse error: {parsed.error}</p>
          )}
        </div>
        <Button
          variant="ghost" size="sm" onClick={() => setExpanded((x) => !x)}
          data-testid={`expand-${row.id}`}
          className="text-white/60 hover:text-white"
        >
          <ChevronRight className={`h-4 w-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </Button>
      </div>

      {expanded && (
        <div className="px-4 pb-5 pt-1 space-y-4 border-t border-white/5">
          {isReview && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Partner slug">
                <input
                  type="text" value={edit.partner_slug}
                  onChange={(e) => setEdit({ ...edit, partner_slug: e.target.value })}
                  data-testid={`edit-slug-${row.id}`}
                  className="w-full bg-black/40 border border-white/15 rounded px-2.5 py-1.5 text-sm text-white"
                  placeholder="emergent / larven / ..."
                />
              </Field>
              <Field label="Revenue USD">
                <input
                  type="number" step="0.01" min="0" value={edit.revenue_usd}
                  onChange={(e) => setEdit({ ...edit, revenue_usd: e.target.value })}
                  data-testid={`edit-revenue-${row.id}`}
                  className="w-full bg-black/40 border border-white/15 rounded px-2.5 py-1.5 text-sm text-white"
                />
              </Field>
              <Field label="Conversions">
                <input
                  type="number" min="1" value={edit.conversions}
                  onChange={(e) => setEdit({ ...edit, conversions: e.target.value })}
                  data-testid={`edit-conversions-${row.id}`}
                  className="w-full bg-black/40 border border-white/15 rounded px-2.5 py-1.5 text-sm text-white"
                />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Year">
                  <input
                    type="number" min="2024" max="2100" value={edit.period_year}
                    onChange={(e) => setEdit({ ...edit, period_year: e.target.value })}
                    data-testid={`edit-year-${row.id}`}
                    className="w-full bg-black/40 border border-white/15 rounded px-2.5 py-1.5 text-sm text-white"
                  />
                </Field>
                <Field label="Month">
                  <input
                    type="number" min="1" max="12" value={edit.period_month}
                    onChange={(e) => setEdit({ ...edit, period_month: e.target.value })}
                    data-testid={`edit-month-${row.id}`}
                    className="w-full bg-black/40 border border-white/15 rounded px-2.5 py-1.5 text-sm text-white"
                  />
                </Field>
              </div>
            </div>
          )}

          <details className="text-xs text-white/60">
            <summary className="cursor-pointer hover:text-white/80 select-none flex items-center gap-1.5">
              <Eye className="h-3 w-3" /> View raw email
            </summary>
            <pre className="mt-2 max-h-60 overflow-y-auto bg-black/40 border border-white/10 rounded-lg p-3 whitespace-pre-wrap font-mono text-[11px] text-white/70 leading-relaxed">
{row.raw_email_text || '(empty)'}
            </pre>
          </details>

          {isReview && (
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button
                variant="outline" size="sm" onClick={dismiss} disabled={busy}
                data-testid={`dismiss-${row.id}`}
                className="border-white/20 text-white hover:bg-white/10"
              >
                <XCircle className="h-3.5 w-3.5 mr-1.5" />
                Dismiss
              </Button>
              <Button
                size="sm" onClick={apply} disabled={busy || !edit.partner_slug?.trim()}
                data-testid={`apply-${row.id}`}
                className="bg-[#00FF94] text-black hover:bg-[#00FF94]/90 font-semibold"
              >
                {busy
                  ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Saving…</>
                  : <><Save className="h-3.5 w-3.5 mr-1.5" />Apply to Partner Revenue</>}
              </Button>
            </div>
          )}

          {!isReview && row.applied_parsed && (
            <p className="text-xs text-white/60">
              Applied {relativeTime(row.applied_at)} ·
              partner=<code className="text-[#00F0FF]">{row.applied_parsed.partner_slug}</code> ·
              ${Number(row.applied_parsed.revenue_usd).toFixed(2)} ·
              {row.applied_parsed.period_year}-{String(row.applied_parsed.period_month).padStart(2, '0')}
            </p>
          )}
        </div>
      )}
    </article>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider font-bold text-white/60 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function StatusPill({ status }) {
  const cfg = {
    pending_review: { label: 'Pending', tone: 'bg-amber-500/15 text-amber-300 border-amber-500/30', Icon: AlertTriangle },
    applied:        { label: 'Applied', tone: 'bg-[#00FF94]/15 text-[#00FF94] border-[#00FF94]/30', Icon: CheckCircle2 },
    dismissed:      { label: 'Dismissed', tone: 'bg-white/5 text-white/40 border-white/10', Icon: XCircle },
  }[status] || { label: status, tone: 'bg-white/5 text-white/60 border-white/10', Icon: Settings2 };
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${cfg.tone}`}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}

function LogTable({ rows }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid="log-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase tracking-wider text-white/60">
            <tr>
              <th className="text-left py-2.5 px-3 font-semibold">Partner</th>
              <th className="text-right py-2.5 px-3 font-semibold">Amount</th>
              <th className="text-center py-2.5 px-3 font-semibold">Period</th>
              <th className="text-left py-2.5 px-3 font-semibold">Subject</th>
              <th className="text-right py-2.5 px-3 font-semibold">Applied</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-white/[0.02]" data-testid={`log-row-${r.id}`}>
                <td className="py-2.5 px-3">
                  <code className="text-[#00F0FF] text-xs">{r.parsed?.partner_slug || '—'}</code>
                  {r.parsed?.partner_name_in_email && r.parsed.partner_name_in_email !== r.parsed.partner_slug && (
                    <span className="ml-2 text-[11px] text-white/40">({r.parsed.partner_name_in_email})</span>
                  )}
                </td>
                <td className="py-2.5 px-3 text-right tabular-nums font-semibold text-[#00FF94]">
                  ${Number(r.parsed?.revenue_usd || 0).toFixed(2)}
                  {(r.parsed?.currency || 'USD') !== 'USD' && (
                    <span className="text-white/40 text-xs ml-1">({r.parsed.currency})</span>
                  )}
                </td>
                <td className="py-2.5 px-3 text-center tabular-nums text-white/70">
                  {r.parsed?.period_year}-{String(r.parsed?.period_month || 1).padStart(2, '0')}
                </td>
                <td className="py-2.5 px-3 text-white/70 truncate max-w-[300px]" title={r.subject}>
                  {r.subject}
                </td>
                <td className="py-2.5 px-3 text-right text-xs text-white/50 whitespace-nowrap">
                  <span className="inline-flex items-center gap-1" title={r.logged_at}>
                    <Clock className="h-3 w-3" />
                    {relativeTime(r.logged_at)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminCommissionFeed;
